import React from 'react';
import {
  useSelector,
  useDispatch,
} from 'react-redux';
import { useReactToPrint } from 'react-to-print';
import { flatten } from 'lodash';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  TablePagination,
  Avatar,
  Tooltip,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from '@mui/material';
import { withStyles } from '@mui/styles';

import {
  changeStatus,
  getEventsExport,
  patchEvent,
  searchEvents,
} from '@pingum/app-state-management/event/actions';
import { getStatuses } from '@pingum/app-state-management/status/actions';

import {
  Add,
  AttachMoney,
  CheckBox,
  CheckBoxOutlineBlank,
  CloudDownload,
  InfoOutlined,
  MoneyOff,
  Print,
  Task,
} from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { setGlobalModals } from '../../../redux/app/actions';
import styles from './styles';
import Filter from './components/Filter';
import LoadingTaskRow from './components/LoadingTaskRow';
import Page from '../../../components/Page';
import PageBody from '../../../components/PageBody';
import TaskStateLabel from '../../../components/TaskStateLabel';
import TaskStatus from '../../../components/TaskStatus';
import User from '../../../components/User';

import { getIconForItem } from '../../../utils/iconHelper';
import { formatDateString } from '../../../utils/dateHelper';
import theme from '../../../utils/theme';
import NavigationBarn from '../components/NavigationBar';
import Horse from '../../../components/Horse';
import { getIcon } from '../../horse/Horse/components/LocationsCard';
import { primary } from '../../../utils/brandColors';
import TaskCalendarEventMenu from '../../../components/Planner/components/TaskCalendarOverview/components/TaskCalendarEventMenu';
import {
  SCHEDULE_MODAL_TYPE,
  TASK_MODAL_TYPE,
} from '../../../redux/app/types';
import { getColorForBillableStatus } from '../../billing/components/BillablesTable';

const TasksComponent = ({
  classes,
  navigate,
  initialFilter,
  permissions,
  hideNav,
  unreadCount,
  chatClient,
  callbackOnAdd,
}) => {
  const fullScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();

  const selectedDashboard = useSelector((state) => state.dashboard.currentFilterDashboard) // Could be set from analytics page

  // State
  const [page, setPage] = React.useState(0);
  const [filter, setFilter] = React.useState((selectedDashboard && selectedDashboard.id > 0 && selectedDashboard.dashboardData) || {
    endDate: null,
    includeArchived: false,
    includeComplete: true,
    searchText: '',
    sortStartDateDesc: true,
    startDate: null,
    statusIds: [],
    tagConditions: [],
    ...initialFilter,
  });
  const componentRef = React.useRef();

  const [tagTypesWithTags, setTagTypesWithTags] = React.useState([]);
  const [flatTagsList, setFlatTagsList] = React.useState([]);
  const [pageSize, setPageSize] = React.useState(25);
  const [openQuickSelectMenu, setOpenQuickSelectMenu] = React.useState(false);
  const [open, setOpen] = React.useState(!fullScreen);
  const [selectedTaskId, setSelectedTaskId] = React.useState(null);

  const eventsPageable = useSelector((state) => state.event.eventsPageable);
  const newEvents = useSelector((state) => state.event.newEvents);
  const isFetchingEvents = useSelector((state) => state.event.isFetchingEvents);
  const statuses = useSelector((state) => state.status.statuses);
  const tagTypes = useSelector((state) => state.tagType.tagTypes);
  const selectedBarn = useSelector((state) => state.barn.selectedBarn);
  const locationsMap = useSelector((state) => state.location.locationsMap);
  const barnHorsesMap = useSelector((state) => state.barn.barnHorsesMap);
  const selectedLocation = useSelector((state) => state.location.selectedLocation);
  const eventData = useSelector((state) => state.eventData.eventData);

  React.useEffect(() => {
    setOpen(!fullScreen)
  }, [fullScreen])

  React.useEffect(() => {
    if (!statuses || statuses.length === 0) {
      dispatch(getStatuses());
    }
  }, []);

  React.useEffect(() => {
    setTagTypesWithTags(tagTypes);
    setFlatTagsList(flatten([...tagTypes.map((tt) => tt.tags)]));
  }, [tagTypes]);

  React.useEffect(() => {
    dispatch(searchEvents(
      filter,
      page,
      pageSize,
      true, // sortStartDateDesc
      true, // reset
    ));
    setSelectedTaskId(null)
  }, [pageSize, page, filter]);

  const applyFilterToSearch = (newFilter) => {
    setPage(0); // will trigger useEffect change
    setFilter(newFilter);
  };

  const handleChangePage = (
    event,
    newPage,
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExport = () => {
    dispatch(getEventsExport(filter));
  }

  const handleOpenScheduleModal = (eventTemplate, isCalendarEvent, replacementSlot) => {
    let taskTagIds = [];

    if (eventTemplate.tags.find((t) => t.id === selectedBarn.barnMetadata.barnTaskTypeTagId)) {
      // this is a barn task so add a location
      taskTagIds = []
    } else if (eventTemplate.tags
      .find((t) => t.id === selectedBarn.barnMetadata.horseTaskTypeTagId || t.id === selectedBarn.barnMetadata.eventTaskTypeTagId)) {
      // horse task so add any selected horses
      if (initialFilter && initialFilter.tagConditions && initialFilter.tagConditions.length === 1) {
        // horse task so add any selected horses
        taskTagIds = [{
          id: parseInt(initialFilter.tagConditions[0], 10),
          tagType: { id: selectedBarn.barnMetadata.horseTagTypeId },
        }];
      } else {
        taskTagIds = [];
      }
    }

    dispatch(setGlobalModals(
      SCHEDULE_MODAL_TYPE,
      null,
      null,
      null,
      {
        locationId: isCalendarEvent && selectedLocation && selectedLocation.id,
        taskTagIds,
        eventTemplateTagTypeFilter: eventTemplate
          ? eventTemplate.id
          : null,
        openInSeries: false,
        startDate: null,
        duration: null,
        assignment: null,
        workflows: replacementSlot && replacementSlot.workflows, // passed in from taskcalendareventmenu
      },
      (reload) => {
        callbackOnAdd();
        if (reload) {
          setPage(0); // Reset data
        }
      },
    ));
  }

  const handleSelectTask = (e, task) => {
    dispatch(setGlobalModals(TASK_MODAL_TYPE, task, null, null, {}, () => {
    }));
  }

  const handleTaskStateChange = (task) => {
    setSelectedTaskId(task.id);
    if (task.completeDate) {
      // reactivate
      dispatch(patchEvent(task.id, { completeEvent: false })).then(() => {
        dispatch(changeStatus(
          task.id,
          {
            statusId: parseInt(selectedBarn.barnMetadata.statusPendingId, 10),
            statusComment: null,
            completeEvent: false,
            assignment: (task.currentEventStatus && task.currentEventStatus.assignment) || '',
          },
          () => {
            setSelectedTaskId(null)
          },
        ));
      });
    } else {
      // complete
      dispatch(changeStatus(
        task.id,
        {
          statusId: parseInt(selectedBarn.barnMetadata.statusEndId, 10),
          statusComment: 'Task marked complete',
          completeEvent: true,
          assignment: (task.currentEventStatus && task.currentEventStatus.assignment) || '',
        },
        () => {
          setSelectedTaskId(null)
        },
      ));
    }
  }

  const handlePrint = useReactToPrint({ content: () => componentRef.current });
  const getPageMargins = () => '@page { margin: 1rem !important; }';
  const getEDNameFromId = (id) => (eventData.find((ed) => ed.id === id) || { name: 'Unknown' }).name

  return (
    <Page>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
        }}
        ref={componentRef}
      >
        {/* <style type="text/css" media="print">{'\
                    @page { size: landscape; }\
                  '}
                </style> */}
        <style>{getPageMargins()}</style>
        {!hideNav && <NavigationBarn navigate={navigate} path="/tasks" permissions={permissions} open={open} setOpen={setOpen} />}
        <PageBody
          navigate={navigate}
          chatClient={chatClient}
          unreadCount={unreadCount}
          width={!hideNav && `calc(100% - ${open
            ? 225
            : 56}px)`}
          disablePadding
        >
          <Grid
            container
            spacing={1}
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Grid item xs={12}>

              <Filter
                filter={filter}
                onFilter={applyFilterToSearch}
                tagTypes={tagTypesWithTags}
                tags={flatTagsList}
                isFetchingEvents={isFetchingEvents}
                onClose={() => {
                  setFilter({
                    endDate: null,
                    includeArchived: false,
                    includeComplete: false,
                    searchText: '',
                    sortStartDateDesc: true,
                    startDate: null,
                    statusIds: [],
                    tagConditions: [],
                    ...initialFilter,
                  })
                }}
              />
            </Grid>
          </Grid>
          <Box sx={{ p: 2 }}>
            <Grid container spacing={6}>
              <Grid
                item
                xs={12}
              >
                <Grid container>
                  <Grid xs={12} item>
                    <Box sx={{ textAlign: 'right' }}>
                      <Button endIcon={<Print />} size="small" sx={{ mr: 2 }} onClick={() => { handlePrint(); }}>
                        Print
                      </Button>
                      <Button endIcon={<CloudDownload />} size="small" sx={{ mr: 2 }} onClick={() => { handleExport(); }}>
                        Export
                      </Button>
                      <Button variant="contained" size="small" endIcon={<Add />} onClick={() => { setOpenQuickSelectMenu(true); }}>
                        Create Task
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                <Paper classes={{ root: classes.paperRoot }}>
                  <Table size="small" className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell varient="head" />
                        <TableCell varient="head">Task</TableCell>
                        <TableCell varient="head">Information</TableCell>
                        <TableCell varient="head">Assignment</TableCell>
                        <TableCell varient="head">Participants</TableCell>
                        <TableCell varient="head">Start Date</TableCell>
                        <TableCell varient="head">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {
                        isFetchingEvents
                          ? [1, 2, 3, 4, 5].map((item) => (
                            <LoadingTaskRow key={`loading-item-${item}`} classes={classes} />
                          ))
                          : ([...newEvents, ...eventsPageable.content].map((task) => {
                            const taskEventData = task.eventData || {};
                            const color = (taskEventData.Color && taskEventData.Color.value) || primary[500]
                            const billableValue = (taskEventData[getEDNameFromId(selectedBarn.barnMetadata.horseBillablePriceEventDataId)]
                              && taskEventData[getEDNameFromId(selectedBarn.barnMetadata.horseBillablePriceEventDataId)].value);
                            const billableStatus = (taskEventData['Billable Status']
                              && taskEventData['Billable Status'].value) || 'Open';
                            const billableReceivable = (taskEventData['Billable Receivable']
                              && taskEventData['Billable Receivable'].value) || 'yes';

                            return (
                              <TableRow
                                className={classes.row}
                                key={`task-item-${task.id}`}
                                onClick={(e) => { handleSelectTask(e, task); }}
                                sx={{
                                  backgroundColor: (task.archived)
                                    ? grey[100]
                                    : undefined,
                                }}
                              >
                                <TableCell
                                  sx={{
                                    borderLeft: `8px solid ${color}`,
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  {
                                    task.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.horseTagTypeId)
                                    && barnHorsesMap[task.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.horseTagTypeId).id] && (
                                      <Horse
                                        horse={barnHorsesMap[task.tags
                                          .find((t) => t.tagType.id === selectedBarn.barnMetadata.horseTagTypeId).id][0].horse}
                                      />
                                    )
                                  }
                                  {
                                    task.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId)
                                    && locationsMap[task.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId).id] && (
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'flex-start',
                                        }}
                                      >
                                        <Avatar
                                          alt={locationsMap[task.tags
                                            .find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId).id][0].name}
                                          sx={{
                                            color: 'action.active',
                                            width: 35,
                                            height: 35,
                                          }}
                                        >{getIcon(locationsMap[task.tags
                                          .find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId).id][0])}
                                        </Avatar>
                                        <Box
                                          sx={{
                                            ml: 1,
                                            width: (fullScreen
                                              ? 125
                                              : 150),
                                          }}
                                        >
                                          <Typography component="h3" noWrap sx={{ lineHeight: '18px' }}>{locationsMap[task.tags.find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId).id][0].name}</Typography>
                                          <Typography
                                            noWrap
                                            component="div"
                                            sx={{
                                              color: '#858585',
                                              fontSize: 14,
                                              lineHeight: '18px',
                                            }}
                                          >{locationsMap[task.tags
                                            .find((t) => t.tagType.id === selectedBarn.barnMetadata.locationTagTypeId).id][0].type}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )
                                  }
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <Box>
                                      <div className={classes.taskNameContainer}>
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                          }}
                                        ><Task sx={{ color }} /><span className={classes.taskName}>{task.name}</span>
                                        </Box>
                                        <TaskStateLabel
                                          archived={!!task.archived}
                                          completed={!!task.completeDate}
                                          dueDate={task.dueDate}
                                          isTaskCreated
                                          hideComplete
                                        />
                                      </div>
                                      <Typography variant="caption" component="div" className={classes.eventIdField}>#{task.id}</Typography>
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  <Tooltip title={task.description}>
                                    <Typography sx={{ maxWidth: 250 }} noWrap>{task.description || (billableValue
                                      ? ''
                                      : '-')}
                                    </Typography>
                                  </Tooltip>
                                  {
                                    billableValue && (
                                      <Chip
                                        size="small"
                                        sx={{ mr: 1 }}
                                        color={getColorForBillableStatus({ status: billableStatus || 'Open' })}
                                        icon={billableReceivable
                                          ? <AttachMoney />
                                          : <MoneyOff />}
                                        variant="outlined"
                                        label={billableValue}
                                      />
                                    )
                                  }
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  {
                                    (task.currentEventStatus && task.currentEventStatus.assignment)
                                      ? (task.currentEventStatus.assignment.split(/(?=@\[)/)
                                        .map((user) => (
                                          <User
                                            key={`event-${task.id}-user-${user.match(/@\[.*\]\((.*)\)/)[1]}`}
                                            chip
                                            user={{
                                              auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
                                              userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
                                            }}
                                            mini={fullScreen}
                                          />
                                        )))
                                      : (
                                        '-'
                                      )
                                  }
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  {
                                    task.scheduleAssignment
                                      ? (task.scheduleAssignment.split(/(?=@\[)/)
                                        .map((user) => (
                                          <User
                                            key={`event-${task.id}-user-${user.match(/@\[.*\]\((.*)\)/)[1]}`}
                                            chip
                                            user={{
                                              auth0UserId: user.match(/@\[.*\]\((.*)\)/)[1],
                                              userFullName: (user && user.substr(2, user.indexOf('](') - 2)) || '',
                                            }}
                                            mini={fullScreen}
                                          />
                                        )))
                                      : (
                                        '-'
                                      )
                                  }
                                </TableCell>
                                <TableCell
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  {formatDateString(task.startDate)}
                                </TableCell>
                                <TableCell
                                  className={classes.statusCell}
                                  sx={{
                                    backgroundColor: (task.completeDate)
                                      ? grey[100]
                                      : undefined,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      flex: 1,
                                    }}
                                  >
                                    {
                                      task.currentEventStatus && (
                                        <TaskStatus
                                          eventStatus={task.currentEventStatus}
                                          color={color}
                                          icon={getIconForItem(statuses, task.currentEventStatus.status.id)}
                                          hideComment
                                          hideDate
                                          archived={!!task.archived}
                                          completed={!!task.completeDate}
                                          showDuration={!task.completeDate}
                                        />
                                      )
                                    }
                                    {
                                      selectedTaskId === task.id
                                        ? (
                                          <IconButton sx={{ mr: 1 }}><CircularProgress size={20} /></IconButton>
                                        )
                                        : (
                                          <IconButton
                                            sx={{ mr: 1 }}
                                            disabled={task.id === selectedTaskId}
                                            onClick={(e) => {
                                              e.stopPropagation(); e.preventDefault(); handleTaskStateChange(task);
                                            }}
                                          >
                                            {
                                              task.completeDate
                                                ? (
                                                  <CheckBox
                                                    sx={{ color }}
                                                  />
                                                )
                                                : (
                                                  <CheckBoxOutlineBlank
                                                    sx={{ color }}
                                                  />
                                                )
                                            }
                                          </IconButton>
                                        )
                                    }
                                  </Box>
                                </TableCell>
                              </TableRow>
                            )
                          }))
                      }
                    </TableBody>
                  </Table>
                  {
                    (!isFetchingEvents && [...newEvents, ...eventsPageable.content].length === 0) && (
                      <Box
                        sx={{
                          textAlign: 'center',
                          m: 4,
                        }}
                      >
                        <InfoOutlined
                          sx={{
                            fontSize: 40,
                            fontWeight: 300,
                          }}
                          color="secondary"
                        />
                        <Typography
                          variant="subtitle1"
                          align="center"
                          component="div"
                          color="secondary"
                          sx={{
                            mt: 2,
                            fontWeight: 300,
                          }}
                        >
                          No Tasks Found
                        </Typography>
                      </Box>
                    )
                  }
                </Paper>
              </Grid>

              <Grid xs={12} sm={6} item>
                <TablePagination
                  sx={{ boarder: 0 }}
                  count={eventsPageable.totalElements || 0}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={pageSize}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Grid>
            </Grid>
          </Box>
          <TaskCalendarEventMenu
            tagsFilter={[]}
            includeBarnTasks={!(initialFilter && initialFilter.tagConditions && initialFilter.tagConditions.length > 0)}
            selectedSlot={(initialFilter && initialFilter.tagConditions && initialFilter.tagConditions.length > 0)
              && { barnHorses: initialFilter.tagConditions }}
            includeHorseTasks
            includeCalendarEvents={false}
            open={openQuickSelectMenu}
            onClose={() => { setOpenQuickSelectMenu(false); }}
            onTaskSelect={(et, replacementSlot) => { setOpenQuickSelectMenu(false); handleOpenScheduleModal(et, false, replacementSlot); }}
            onCalendarEventSelect={(et) => { setOpenQuickSelectMenu(false); handleOpenScheduleModal(et, true); }}
          />
        </PageBody>
      </Box>
    </Page>
  );
};

TasksComponent.propTypes = {
  classes: PropTypes.object,
  initialFilter: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  permissions: PropTypes.object.isRequired,
  hideNav: PropTypes.bool,
  chatClient: PropTypes.any,
  unreadCount: PropTypes.number,
  callbackOnAdd: PropTypes.func,
};
TasksComponent.defaultProps = {
  classes: {},
  initialFilter: {},
  hideNav: false,
  chatClient: null,
  unreadCount: 0,
  callbackOnAdd: () => { },
};

export default withStyles(styles)(TasksComponent);
